export class ApiError extends Error {

  constructor(
    public readonly status: number,
    message: string,
    public readonly data?: unknown,
    /** A stable, machine-readable reason some services send (e.g. CMS 'level_locked'). */
    public readonly code?: string
  ) {

    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiResponse<T = null> {
  status: 'success' | 'error'
  message: string
  data: T
}

/**
 * First-touch campaign attribution (the es_utm cookie, @repo/auth/attribution).
 * Sent with registration (UMS) and practice-pack checkout (BMS); each
 * service validates it again. Campaign labels and a page path only.
 */
export interface CampaignAttributionInput {
  utmSource: string
  utmMedium?: string | null
  utmCampaign?: string | null
  landingPath: string
  /** Unix seconds. */
  firstSeenAt?: number | null
}

/** One SAST day x utm_source row of an admin launch-numbers aggregate. */
export interface LaunchDayRow {
  day: string
  utmSource: string
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: Pagination
}

export interface Pagination {
  /** Total matching records before pagination */
  total: number
  /** Max records per page */
  limit: number
  /** Records skipped */
  offset: number
  /** Total pages (ceil(total / limit)) */
  pages: number
}

export interface ValidationErrorResponse {
  status: 'error'
  message: string
  data: Record<string, string[]>
}

export type ApiRequestFn = <T>(path: string, init?: RequestInit, token?: string) => Promise<T>;

// A retry of the SAME attempt should not create a second invoice, enquiry,
// application or email. Callers pass the key they generated for the attempt;
// the backend replays its first answer for a repeat, and refuses the key if
// the body changed. Optional throughout: no key, no behaviour change.
export const idempotencyHeader = (key?: string): Record<string, string> =>
  key ? {
    'Idempotency-Key': key
  } : {};

// Browser fetch() has no built-in timeout - a request that never gets a
// response (backend mid-restart, dropped connection, etc.) hangs forever
// with no error, ever. Callers that fan out to multiple services in
// parallel (e.g. listAuditEventsAllServices) are especially exposed: one
// hung request stalls the entire Promise.all indefinitely, even though
// every other service answered fine. 20s is long enough for any real
// response on this platform's slowest known endpoints, short enough that a
// genuinely hung request fails within one page-load's patience instead of
// spinning forever.
const REQUEST_TIMEOUT_MS = 20000;

// publicBaseUrl is a relative path (e.g. "/api/bms") that only resolves
// correctly in a browser, against the page's own origin via nginx's proxy.
// Server Components run fetch() on the Node server itself, where a
// relative URL isn't valid and throws immediately - silently, if the
// caller wraps it in a try/catch. serverBaseUrl (an absolute
// http://localhost:<port>/api/v1 URL, only reachable from the box itself)
// is used instead whenever this runs on the server.
export function createApiClient(
  publicBaseUrl: string,
  serverBaseUrl?: string
): ApiRequestFn {

  return async function apiRequest<T>(
    path: string,
    init?: RequestInit,
    token?: string
  ): Promise<T> {

    const baseUrl = typeof window === 'undefined'
      ? serverBaseUrl ?? publicBaseUrl
      : publicBaseUrl;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? {
        Authorization: `Bearer ${ token }`
      } : {}),
      ...(init?.headers as Record<string, string> | undefined)
    };

    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);

    let response: Response;

    try {

      response = await fetch(`${ baseUrl }${ path }`, {
        ...init,
        headers,
        signal: timeoutController.signal
      });
    } catch (err) {

      if (err instanceof Error && err.name === 'AbortError') {

        throw new ApiError(0, `Request to ${ path } timed out after ${ REQUEST_TIMEOUT_MS }ms`);
      }

      throw err;
    } finally {

      clearTimeout(timeoutId);
    }

    if (response.status === 204) {

      return undefined as T;
    }

    const body: unknown = await response.json();

    if (!response.ok) {

      const err = body as {
        message?: string
        data?: unknown
        code?: unknown
      };
      throw new ApiError(
        response.status,
        err?.message ?? response.statusText,
        err?.data,
        typeof err?.code === 'string' ? err.code : undefined
      );
    }

    return body as T;
  };
}
